// Story 7.10 — mutation hook for POST /api/v1/creators/me/posts
// (Story 7.11's endpoint). Discriminated-union body per post kind;
// fresh idempotency key per submit; invalidates ["feed"] on success so
// the creator's own feed shows the post on next refresh.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type CreatePostVariables =
  | { kind: "text"; body: string }
  | { kind: "text_image"; body: string; media_blob_path: string }
  | { kind: "twitch_clip"; twitch_url: string; body: string | null }
  | { kind: "youtube_short"; youtube_url: string; body: string | null };

export interface CreatePostResponse {
  id: string;
  published_at: string;
  source_kind: string;
}

export interface CreatePostError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCreatePost(
  variables: CreatePostVariables,
): Promise<CreatePostResponse> {
  const response = await fetch("/api/v1/creators/me/posts", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreatePostError = {
      code: "CREATE_POST_FAILED",
      message: `Post failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as CreatePostResponse;
}

export function useCreatePost(): UseMutationResult<
  CreatePostResponse,
  CreatePostError,
  CreatePostVariables
> {
  const queryClient = useQueryClient();
  return useMutation<CreatePostResponse, CreatePostError, CreatePostVariables>({
    mutationFn: postCreatePost,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
